'use client'

import { useEffect, useCallback } from 'react'
import * as Sentry from '@sentry/nextjs'

const { logger } = Sentry
const STORAGE_KEY = 'mullins-bus-form-data'

export interface FormData {
  name: string
  email: string
  phone: string
}

/**
 * Hook to persist form data (name, email, phone) to localStorage
 * and restore it when the component mounts
 */
export function useFormPersistence() {
  // Load saved form data from localStorage
  const loadFormData = useCallback((): FormData => {
    if (typeof window === 'undefined') {
      return { name: '', email: '', phone: '' }
    }

    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored) as FormData
        return {
          name: parsed.name || '',
          email: parsed.email || '',
          phone: parsed.phone || '',
        }
      }
    } catch (error) {
      Sentry.captureException(error)
      logger.error(logger.fmt`Failed to load form data from localStorage: ${error}`)
    }

    return { name: '', email: '', phone: '' }
  }, [])

  // Save form data to localStorage
  const saveFormData = useCallback((data: Partial<FormData>) => {
    if (typeof window === 'undefined') return

    try {
      const existing = loadFormData()
      const updated = { ...existing, ...data }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
    } catch (error) {
      Sentry.captureException(error)
      logger.error(logger.fmt`Failed to save form data to localStorage: ${error}`)
    }
  }, [loadFormData])

  // Save a single field when user leaves it (onBlur)
  const saveField = useCallback((field: keyof FormData, value: string) => {
    saveFormData({ [field]: value })
  }, [saveFormData])

  return {
    loadFormData,
    saveFormData,
    saveField,
  }
}



